import React, { useContext, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileImport, faSpinner } from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { useQueryClient } from "react-query";
import { useLanguage } from "../context/LanguageContext";
import { FetchContext } from "../context/FetchContext";
import { useUpdateTranslation } from "../queries/use-update-translation";
import { useCreateKey } from "../queries/use-create-key";

export const ImportButton = ({ projectId, version }) => {
  const { languages } = useLanguage();
  const fetchContext = useContext(FetchContext);
  const [loading, setLoading] = useState(false);

  const hiddenFileInput = React.useRef(null);

  const { mutateAsync } = useUpdateTranslation(projectId, version);
  const { mutateAsync: createKey } = useCreateKey(projectId, version);
  const queryClient = useQueryClient();

  const handleClick = () => {
    hiddenFileInput.current.click();
  };
  const handleChange = async (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = async function (e) {
      setLoading(true);
      const workbook = XLSX.read(e.target.result);

      const { data } = await fetchContext.authAxios.get(
        `/projects/${projectId}/versions/${version}/keys`
      );

      const updates = [];
      const creates = [];
      workbook.SheetNames.forEach(async (sheetName) => {
        // Here is your object
        const sheetData = XLSX.utils.sheet_to_row_object_array(
          workbook.Sheets[sheetName]
        );
        const langMap = languages.reduce((acc, lang) => {
          acc[lang.id] = lang.iso;
          return acc;
        }, {});
        const originalMaps = data.reduce((acc, d) => {
          acc[d.name] = d;
          return acc;
        }, {});
        sheetData.forEach((row) => {
          const original = originalMaps[row.key];
          if (original) {
            original.translations.forEach((t) => {
              const newTranslation = row[langMap[t.languageId]];
              if (
                newTranslation !== undefined &&
                newTranslation !== t.translation
              ) {
                updates.push({
                  original: t,
                  new: newTranslation.toString(),
                  key: original,
                });
              }
            });
          } else {
            creates.push({
              name: row.key,
              translations: languages.map((lang) => ({
                languageId: lang.id,
                translation: (row[lang.iso] ?? "").toString(),
              })),
            });
          }
        });
      });
      try {
        // eslint-disable-next-line no-restricted-syntax
        for (const update of updates) {
          // eslint-disable-next-line no-await-in-loop
          await mutateAsync({
            text: update.new,
            keyId: update.key.id,
            translationId: update.original.id,
          });
        }
        // eslint-disable-next-line no-restricted-syntax
        for (const create of creates) {
          // eslint-disable-next-line no-await-in-loop
          await createKey(create);
        }
        toast.success(
          `Import success, ${creates.length} created, ${updates.length} entries updated`
        );
      } catch (e) {
        console.log(e);
        toast.error("Import failed");
      } finally {
        const queryKey = [
          "project",
          projectId?.toString(),
          "version",
          version?.toString(),
          "keys",
        ];
        await queryClient.invalidateQueries(queryKey, { exact: false });
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };
  return (
    <>
      <input
        type="file"
        ref={hiddenFileInput}
        onChange={handleChange}
        style={{ display: "none" }}
      />
      <button
        type="button"
        className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-purple-500 text-white text-sm font-semibold shadow-sm hover:opacity-95 transition disabled:opacity-60"
        onClick={() => handleClick()}
        disabled={loading}
      >
        <FontAwesomeIcon
          className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
          icon={loading ? faSpinner : faFileImport}
        />
        {!loading ? "Import" : "Loading"}
      </button>
    </>
  );
};
